<template>
  <div
    v-if="sortedPatientDocuments.length"
    class="scrollbar-visible"
    :class="{
      'c-documents-per-category': true,
      'c-documents-per-category--selectable': selectable,
    }"
  >
    <div v-for="c in grouped ? sortedPatientDocuments : ungroupedDocuments" v-bind:key="c.category.id">
      <v-row
        v-if="grouped"
        class="
          c-documents-per-category__row c-documents-per-category__header-row
          pl-2
        "
      >
        <v-col cols="1" align-self="center">
          <v-row justify="end">
            <category-icon
              :categoryKey="c.category.key.toUpperCase()"
              :size="20"
              :iconColor="$color.coro.gray"
              class="mr-2"
            />
          </v-row>
        </v-col>
        <v-col cols="11">
          {{ c.category.name }}
        </v-col>
      </v-row>

      <v-row
        v-for="doc in c.docs"
        v-bind:key="doc.document.id"
        :class="{
          'c-documents-per-category__row': true,
          'c-documents-per-category__row--active':
            activePatientDocumentId &&
            doc.patientDocumentId === activePatientDocumentId,
        }"
        @click="(event) => $emit('click:categoryrow', { event, doc })"
        align="center"
      >
        <v-col cols="0" md="1">
            <category-icon
              v-if="!grouped"
              :categoryKey="doc.document.mainCategory.key.toUpperCase()"
              :size="20"
              :iconColor="$color.coro.gray"
              class="pl-2"
            />
        </v-col>
        <v-col cols="0" md="1" :class="{'pr-1': !grouped}">
          <v-row justify="end">
            <smart-icon
              icon="mdi-movie-outline"
              iconColor="rgba(0, 0, 0, 0.87)"
              :size="20"
              v-if="getContentType(doc.document.categories) === 'VIDEO'"
              :class="{'mr-2': grouped}"
            />
            <smart-icon
              icon="mdi-image-outline"
              iconColor="rgba(0, 0, 0, 0.87)"
              :size="20"
              v-if="getContentType(doc.document.categories) === 'INFOGRAPHIC'"
              :class="{'mr-2': grouped}"
            />
            <smart-icon
              icon="mdi-file-document-outline"
              iconColor="rgba(0, 0, 0, 0.87)"
              :size="20"
              v-if="getContentType(doc.document.categories) === 'TEXT'"
              :class="{'mr-2': grouped}"
            />
          </v-row>
        </v-col>
        <v-col cols="12" md="6">
          {{ doc.document.title }}
        </v-col>
        <v-col
          cols="12"
          md="4"
          align="end"
          class="pl-3 d-flex align-center justify-end"
          :class="[hideIcons ? 'pr-5' : 'pr-8']"
        >
          <span
            class="mr-3"
            v-if="doc.timesOpened > 0"
          >
            <smart-icon
              icon="mdi-eye-outline"
              :iconColor="$color.coro['info-500']"
            />
          </span>
          <div
            v-if="doc.documentTime && showDate && eventsInfo[doc.patientDocumentId]"
            class="d-inline-flex flex-column"
          >
            <span
              v-for="event in eventsInfo[doc.patientDocumentId]"
              style="font-size: 0.9rem; white-space: nowrap"
              class="mr-1"
              :key="event.date"
            >
              {{ event.count > 1 ? `${event.count}x` : '' }}
              <span class="px-1"></span>
              {{ event.date | moment }}
            </span>
          </div>
          <span
            v-else-if="doc.documentTime && showDate"
            style="font-size: 0.9rem"
            class="mr-1"
          >
            {{ doc.documentTime | moment }}
          </span>
          <v-btn
              v-if="doc.topicSelected && !hideIcons"
              class="mr-1 c-documents-per-category__icon ignore-disabled-styles"
              fab text
              x-small
              :color="$color.coro['purple']"
              disabled
          >
            <smart-icon
              icon="eva-person-outline"
              :iconColor="$color.coro['purple']"
            />
          </v-btn>
          <v-btn
              v-if="doc.recommended && !hideIcons"
              class="mr-1 c-documents-per-category__icon ignore-disabled-styles"
              fab text
              x-small
              :color="$color.coro['green']"
              @click.stop="removeChoiceOfCaregiver(doc.document.id)"
          >
            <smart-icon
              icon="eva-person-outline"
              :iconColor="$color.coro['green']"
            />
          </v-btn>
          <v-btn
              v-if="!doc.recommended && !doc.topicSelected && !hideIcons"
              class="mr-1 c-documents-per-category__icon ignore-disabled-styles"
              fab text
              x-small
              :color="$color.coro['blue']"
              disabled
          >
            <smart-icon
              icon="eva-person-outline"
              :iconColor="$color.coro['blue']"
            />
          </v-btn>
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  v-if="!hideIcons"
                  class="c-documents-per-category__icon"
                  fab text
                  x-small
                  :iconColor="$color.coro['black']"
                  v-on="on"
                  v-bind="attrs"
                  :disabled="disabledIds.includes(doc.document.id)"
                  @click.stop="(event) => $emit('click:share', { event, doc })"
                >
                  <smart-icon
                    icon="mdi-share-outline"
                    :iconColor="$color.coro['black']"
                  />
                </v-btn>
              </template>
              {{ $ucfirst($t("knowledge.sendnotification")) }}
            </v-tooltip>
        </v-col>
      </v-row>
    </div>
  </div>
</template>

<script>
import moment from "moment";
import CategoryIcon from "./CategoryIcon.vue";
import SmartIcon from "./SmartIcon.vue";

/**
 * Component DocumentsPerCategory.vue
 *
 * @module DocumentsPerCategory
 * @date
 * @author
 * @copyright UniWeb bvba 2022
 *
 * @link https://gitlab.uniweb.be/cor/4.04-caregiver-dashboard
 *
 * @coropreventionSW [SW-4.8] app/src/APP_DASHBOARD/templates/components/Parts/DocumentsPerCategory.vue
 * @see https://docs.google.com/document/d/1iOpHH151s_ABr-DniJIc__q-GHpXJJVq/edit#heading=h.a0z8d9xd3xr7
 *
 */

export default {
  name: "DocumentsPerCategory",
  components: { CategoryIcon, SmartIcon },
  props: {
    // if documents are selectable (adds hover animation)
    selectable: {
      type: Boolean,
      default: () => false,
    },

    // the sorted patient documents
    sortedPatientDocuments: {
      type: Array,
    },

    // the patientDocumentId of the currently active patient document (adds active css class)
    activePatientDocumentId: {
      type: Number,
    },

    // if icons (indication of "Choice Of", "Send to patient") are shown
    hideIcons: {
      type: Boolean,
      default: () => false,
    },

    // if dates are shown
    showDate: {
      type: Boolean,
      default: () => false,
    },

    // all patient documents event info
    eventsInfo: {
      type: Object
    },

    // group by category
    grouped: {
      type: Boolean,
      default: () => true,
    },

    disabledIds: {
      type: Array,
      default: () => []
    }
  },
  filters: {
    moment: function (date) {
      return date ? moment(date).format("DD/MM/YYYY") : "";
    },
  },
  methods: {
    getContentType: (categories) => {
      let found = categories.filter((cat) => cat.type == "CONTENT_TYPE");

      return found.length ? found[0].key : 'TEXT';
    },
    removeChoiceOfCaregiver(id) {
      this.$emit('removeChoiceOfCaregiver', id, false)
    }
  },
  computed: {
    ungroupedDocuments() {

      const ungrouped = this.sortedPatientDocuments.map(i => Object.values(i.docs)).flat();
      const sortedByDate = ungrouped.sort((a,b) =>
          moment(a.documentTime).valueOf() - moment(b.documentTime).valueOf())

      const formated = [{
          category: {
            id: 'all_categories',
            key: 'ALL'
          },
          docs: sortedByDate
        }
      ]

      return formated;
    }
  }
};
</script>

<style lang="scss">
@import "../../../../scss/colors";

// mobile first
.c-documents-per-category {
  max-height: 248px;
  overflow-y: auto;
  overflow-x: hidden;
}

.c-documents-per-category__header-row {
  color: map-get($coroColors, gray);
}

.c-documents-per-category__row {
  border-bottom: 1px solid map-get($coroColors, lightgray2);
}

.c-documents-per-category__row--active {
  color: map-get($coroColors, blue) !important;
  background-color: map-get($coroColors, lightblue) !important;
}

// -------------------------------------------------- BEM modifiers ------------------------------------------------
.c-documents-per-category--selectable {
  transition: background-color 0.3s;

  .c-documents-per-category__row {
    &:not(.c-documents-per-category__header-row) {
      cursor: pointer;

      &:hover {
        background-color: map-get($coroColors, lightgray);
      }
    }
  }
}

.c-documents-per-category__icon:not(.c-documents-per-category__icon:hover) {
  color: transparent !important;
}
</style>